class CartGiftSync {
  constructor() {
    this.SUBSCRIPTION_PRODUCT_ID = window.subscriptionGiftSettings?.subscriptionProductId || '7722874798131';
    this.FREE_GIFT_PRODUCT_ID = window.subscriptionGiftSettings?.giftProductId || '7723756683315';
    this.FREE_GIFT_PRODUCT_HANDLE = window.subscriptionGiftSettings?.giftProductHandle || 'bonus-shaker-bottle';
    this.ENABLED = window.subscriptionGiftSettings?.enabled !== false;
    
    this.subscriptionItem = null;
    this.giftItem = null;
    this.isSyncing = false;
    
    if (this.ENABLED) {
      this.init();
    }
  }

  init() {
    this.syncOnLoad();
    this.observeCartChanges();
    this.attachCartListeners();
  }

  async syncOnLoad() {
    const state = this.loadState();
    
    if (!state || !state.isSubscriptionSelected) {
      return;
    }

    const timeSinceLastUpdate = Date.now() - (state.timestamp || 0);
    if (timeSinceLastUpdate > 3600000) {
      return;
    }

    await this.syncGiftWithSubscription();
  }

  async syncGiftWithSubscription() {
    if (this.isSyncing) {
      console.log('Already syncing, skipping...');
      return;
    }

    this.isSyncing = true;
    this.setLoadingState(true);

    try {
      const cart = await this.getCart();
      
      this.subscriptionItem = cart.items.find(item => 
        item.product_id.toString() === this.SUBSCRIPTION_PRODUCT_ID &&
        item.selling_plan_allocation
      );

      this.giftItem = cart.items.find(item => 
        item.product_id.toString() === this.FREE_GIFT_PRODUCT_ID
      );

      if (this.subscriptionItem && !this.giftItem) {
        await this.addGiftToCart(this.subscriptionItem.quantity);
      } else if (this.subscriptionItem && this.giftItem) {
        if (this.giftItem.quantity !== this.subscriptionItem.quantity) {
          await this.updateGiftQuantity(this.giftItem.key, this.subscriptionItem.quantity);
        }
      } else if (!this.subscriptionItem && this.giftItem) {
        await this.removeGiftFromCart(this.giftItem.key);
      }

      await new Promise(resolve => setTimeout(resolve, 300));
    } catch (error) {
      console.error('Error syncing gift with subscription:', error);
    } finally {
      this.isSyncing = false;
      this.setLoadingState(false);
    }
  }

  setLoadingState(isLoading) {
    const checkoutButtons = document.querySelectorAll('[name="checkout"], .cart__checkout-button, button[type="submit"][name="checkout"]');
    
    checkoutButtons.forEach(button => {
      if (isLoading) {
        button.setAttribute('disabled', 'disabled');
        button.style.opacity = '0.5';
        button.style.pointerEvents = 'none';
      } else {
        button.removeAttribute('disabled');
        button.style.opacity = '';
        button.style.pointerEvents = '';
      }
    });

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      if (isLoading) {
        cartDrawer.classList.add('cart-drawer--loading');
      } else {
        cartDrawer.classList.remove('cart-drawer--loading');
      }
    }
  }

  observeCartChanges() {
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
        setTimeout(() => this.syncGiftWithSubscription(), 300);
      });
    }

    const cartForm = document.querySelector('form[action="/cart"]');
    if (cartForm) {
      cartForm.addEventListener('change', (e) => {
        if (e.target.name === 'updates[]' || e.target.classList.contains('quantity__input')) {
          setTimeout(() => this.syncGiftWithSubscription(), 500);
        }
      });
    }
  }

  attachCartListeners() {
    this.interceptCheckoutButton();

    document.addEventListener('click', (e) => {
      if (e.target.matches('.quantity__button, [name="minus"], [name="plus"]')) {
        setTimeout(() => this.syncGiftWithSubscription(), 500);
      }

      if (e.target.matches('cart-remove-button, .cart-item__remove, [href*="/cart/change"]')) {
        setTimeout(() => this.syncGiftWithSubscription(), 500);
      }
    });

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList' && mutation.target.matches('.cart-items, cart-items, .cart-drawer__items')) {
          setTimeout(() => this.syncGiftWithSubscription(), 300);
          break;
        }
      }
    });

    const cartContainer = document.querySelector('.cart-items, cart-items, .cart-drawer__items');
    if (cartContainer) {
      observer.observe(cartContainer, {
        childList: true,
        subtree: true
      });
    }
  }

  interceptCheckoutButton() {
    document.addEventListener('click', (e) => {
      const checkoutButton = e.target.closest('[name="checkout"], .cart__checkout-button, button[type="submit"][name="checkout"]');
      
      if (checkoutButton && this.isSyncing) {
        e.preventDefault();
        e.stopPropagation();
        console.log('Checkout blocked: Gift sync in progress');
        return false;
      }
    }, true);
  }

  async addGiftToCart(quantity) {
    try {
      const productResponse = await fetch(`/products/${this.FREE_GIFT_PRODUCT_HANDLE}.js`);
      const product = await productResponse.json();
      
      if (product.variants && product.variants.length > 0) {
        const variantId = product.variants[0].id;
        
        localStorage.setItem('allowFreeGiftAdd', 'true');
        
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            id: variantId,
            quantity: quantity
          })
        });

        if (response.ok) {
          await this.refreshCart();
        }
      }
    } catch (error) {
      console.error('Error adding gift to cart:', error);
    }
  }

  async updateGiftQuantity(lineItemKey, quantity) {
    try {
      console.log('Updating gift quantity:', lineItemKey, 'to', quantity);
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: lineItemKey,
          quantity: quantity
        })
      });

      if (response.ok) {
        await this.refreshCart();
      } else {
        const errorText = await response.text();
        console.error('Error updating gift quantity:', response.status, errorText);
      }
    } catch (error) {
      console.error('Error updating gift quantity:', error);
    }
  }

  async removeGiftFromCart(lineItemKey) {
    try {
      console.log('Removing gift from cart:', lineItemKey);
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: lineItemKey,
          quantity: 0
        })
      });

      if (response.ok) {
        await this.refreshCart();
        localStorage.removeItem('subscriptionGiftState');
      } else {
        const errorText = await response.text();
        console.error('Error removing gift:', response.status, errorText);
      }
    } catch (error) {
      console.error('Error removing gift from cart:', error);
    }
  }

  async getCart() {
    const response = await fetch('/cart.js');
    return await response.json();
  }

  async refreshCart() {
    try {
      const cart = await this.getCart();
      
      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');

      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        const sections = cartDrawer.getSectionsToRender().map((section) => section.id);
        const sectionsUrl = `${window.location.pathname}?sections=${sections.join(',')}`;
        
        const response = await fetch(sectionsUrl);
        const sectionsData = await response.json();
        
        const cartResponse = {
          ...cart,
          sections: sectionsData
        };

        if (cart.item_count === 0) {
          cartDrawer.classList.add('is-empty');
        } else {
          cartDrawer.classList.remove('is-empty');
        }

        if (typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(cartResponse);
        }
      } else if (cartNotification && typeof cartNotification.getSectionsToRender === 'function') {
        const sections = cartNotification.getSectionsToRender().map((section) => section.id);
        const sectionsUrl = `${window.location.pathname}?sections=${sections.join(',')}`;
        
        const response = await fetch(sectionsUrl);
        const sectionsData = await response.json();
        
        const cartResponse = {
          ...cart,
          sections: sectionsData
        };

        if (cart.item_count === 0) {
          cartNotification.classList.add('is-empty');
        } else {
          cartNotification.classList.remove('is-empty');
        }

        if (typeof cartNotification.renderContents === 'function') {
          cartNotification.renderContents(cartResponse);
        }
      } else {
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { 
            source: 'cart-gift-sync',
            cartData: cart
          });
        }
      }

      const cartItems = document.querySelector('cart-items');
      if (cartItems && cart.item_count === 0) {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }

  loadState() {
    try {
      const stateStr = localStorage.getItem('subscriptionGiftState');
      return stateStr ? JSON.parse(stateStr) : null;
    } catch (error) {
      return null;
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartGiftSync();
  });
} else {
  new CartGiftSync();
}

